/**
 * Pure layout helper for the FK Breadcrumb multi-row tree rendering.
 *
 * The layout is rooted in TREE SHAPE, not in the active node. Row 0 is the
 * "spine" of the tree (root → `childIds[0]` → `childIds[0]` → … → leaf).
 * For every node in any already-placed row, each `childIds[i ≥ 1]` becomes
 * its own row that also extends down its own first-child spine.
 *
 *   customers ─▸ #42 ─▸ addresses ─▸ #7   (row 0 — the spine)
 *                └────▸ orders ─▸ #99     (row 1 — off-spine fork from #42)
 *
 * Activation never reshuffles rows; the active chip is highlighted in place.
 * Output is stable in DFS-by-childIds order: emit the spine first, then for
 * each spine node walk its `childIds[i ≥ 1]` in declaration order, recursing
 * into each as a new spine fragment.
 *
 * The helper is pure / memoizable / testable without React.
 */

import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
} from '../../../core/types/workspaceTypes';

export type TreeLayoutRow = {
  /** Node IDs on this row, left-to-right. */
  readonly row: ReadonlyArray<NavigationNodeId>;
  /**
   * Node on a PRIOR row whose chip "owns" the connector arrowing into
   * this row's first chip. `null` for row 0 (the spine).
   */
  readonly forkParentId: NavigationNodeId | null;
  /** True only for row 0 — the tree spine. */
  readonly isSpine: boolean;
};

export type TreeLayout = ReadonlyArray<TreeLayoutRow>;

/** Walk node → childIds[0] → childIds[0] → … → leaf and return ids in order. */
function spineFrom(
  tree: NavigationTree,
  startId: NavigationNodeId,
): NavigationNodeId[] {
  const out: NavigationNodeId[] = [];
  let cur: NavigationNode | undefined = tree.nodes[startId];
  while (cur) {
    out.push(cur.id);
    const nextId: NavigationNodeId | undefined = cur.childIds[0];
    cur = nextId ? tree.nodes[nextId] : undefined;
  }
  return out;
}

/**
 * Compute a multi-row layout for the breadcrumb tree.
 *
 * Algorithm:
 *  1. Row 0 = spine from root (root → childIds[0] → …).
 *  2. For each node N already placed on a prior row — visiting that row's
 *     spine RIGHT-to-LEFT (deepest first) — walk N.childIds[i ≥ 1] in
 *     declaration order; for each off-spine child C, emit a new row that
 *     itself extends down its own first-child spine. The off-spine row's
 *     `forkParentId` is N (the prior-row node whose chip owns the connector).
 *  3. Recursion continues until every node in the tree has been placed
 *     exactly once.
 *
 * The right-to-left visit order ensures that **the deepest fork along the
 * spine appears on row 1, directly under its parent** — and shallower forks
 * (e.g. siblings of the root's first child) cascade down to lower rows. Without
 * this order, sibling rows of a shallow fork stack closer to the spine than
 * sibling rows of a deeper fork, which makes "which node does this branch come
 * from?" visually ambiguous when there are two branches anchored at different
 * depths along the same spine.
 *
 * Siblings sharing the SAME fork-parent still emit in `childIds` declaration
 * order — only the cross-fork-depth ordering reverses.
 */
export function computeTreeLayout(tree: NavigationTree): TreeLayout {
  const rows: TreeLayoutRow[] = [];
  const placed = new Set<NavigationNodeId>();

  // Emit one row (spine of `startId`), recording placement, then recurse into
  // each row-node's off-spine children to emit their own rows.
  const emitRow = (
    startId: NavigationNodeId,
    forkParentId: NavigationNodeId | null,
  ) => {
    const spineIds = spineFrom(tree, startId);
    rows.push({
      row: spineIds,
      forkParentId,
      isSpine: forkParentId === null,
    });
    for (const id of spineIds) placed.add(id);

    // Walk the row RIGHT-to-LEFT so deeper fork-parents are processed first
    // (their off-spine branches land on earlier rows, right under the spine).
    // Within a single fork-parent's branches, declaration order is preserved.
    for (let s = spineIds.length - 1; s >= 0; s -= 1) {
      const node = tree.nodes[spineIds[s]];
      if (!node) continue;
      for (let i = 1; i < node.childIds.length; i += 1) {
        const childId = node.childIds[i];
        if (placed.has(childId)) continue;
        emitRow(childId, node.id);
      }
    }
  };

  emitRow(tree.rootId, null);
  return rows;
}

/**
 * Per-chip "anchor" describing what L-arrow shape the overlay should draw
 * around it.
 *
 *  - `forkParentId` is the chip whose right edge owns the trunk.
 *  - `sameRowNextId` is the next visible chip on the SAME row (i.e. the chip
 *    the inline `→` separator would normally point at). `null` when the
 *    fork-parent is the last visible chip on its row.
 *  - `downTargetIds` is the first-visible chip of every off-spine row whose
 *    `forkParentId` matches this chip. Multiple targets at the same X column
 *    naturally share the trunk (their vertical legs overlap).
 *
 * Computed from POST-filter rows so join-collapsed chips don't leak into the
 * anchor set, and so chips that have no visible same-row neighbour are
 * correctly marked `sameRowNextId: null`.
 */
export type ForkAnchor = {
  readonly forkParentId: NavigationNodeId;
  readonly sameRowNextId: NavigationNodeId | null;
  readonly downTargetIds: ReadonlyArray<NavigationNodeId>;
};

/**
 * Group the layout rows into per-fork-parent anchors. Iteration order is
 * stable: anchors are emitted in the order their fork-parents first appear
 * (top-to-bottom, left-to-right) across the rows so the overlay's draw
 * order is predictable.
 */
export function computeForkAnchors(
  rows: ReadonlyArray<TreeLayoutRow>,
): ReadonlyArray<ForkAnchor> {
  // First pass — collect off-spine targets per fork-parent.
  const downTargetsByParent = new Map<NavigationNodeId, NavigationNodeId[]>();
  for (const row of rows) {
    if (row.isSpine) continue;
    if (row.forkParentId === null) continue;
    const firstId = row.row[0];
    if (!firstId) continue;
    const arr = downTargetsByParent.get(row.forkParentId) ?? [];
    arr.push(firstId);
    downTargetsByParent.set(row.forkParentId, arr);
  }

  // Second pass — for every chip that owns down-targets, find its
  // sameRowNextId by locating it within the rows array.
  const anchors: ForkAnchor[] = [];
  const seen = new Set<NavigationNodeId>();
  for (const row of rows) {
    for (let i = 0; i < row.row.length; i += 1) {
      const id = row.row[i];
      if (seen.has(id)) continue;
      const downTargets = downTargetsByParent.get(id);
      if (!downTargets || downTargets.length === 0) continue;
      anchors.push({
        forkParentId: id,
        sameRowNextId: row.row[i + 1] ?? null,
        downTargetIds: downTargets,
      });
      seen.add(id);
    }
  }
  return anchors;
}

/** Set of chip ids that are fork-parents. Cheap membership check for the row component. */
export function forkParentIdSet(
  anchors: ReadonlyArray<ForkAnchor>,
): ReadonlySet<NavigationNodeId> {
  const set = new Set<NavigationNodeId>();
  for (const a of anchors) set.add(a.forkParentId);
  return set;
}

/**
 * Filter join-collapsed table nodes out of every row when the per-tab
 * "Show join tables" toggle is OFF. Preserves row ordering; an empty row
 * may still need to be dropped by the caller.
 */
export function filterJoinCollapsedRows(
  layout: ReadonlyArray<TreeLayoutRow>,
  tree: NavigationTree,
  showJoinTables: boolean,
): ReadonlyArray<TreeLayoutRow> {
  if (showJoinTables) return layout;
  return layout.map((r) => ({
    ...r,
    row: r.row.filter((id) => {
      const n = tree.nodes[id];
      return !(n && n.kind === 'table' && n.isJoinCollapsed);
    }),
  }));
}

